import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmCalibreClient, FirearmCalibreDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmCalibreClient = () => new FirearmCalibreClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns (all calibres)
export const useFirearmCalibres = () =>
  useQuery({  
    queryKey: ['firearmCalibres'],
    queryFn: async () => {
      const response = await getFirearmCalibreClient().searchFirearmCalibres({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Cached lookup for dropdowns (filtered by specific)
export const useFirearmCalibersBySpecific = (firearmSpecificId: string | undefined) =>
  useQuery({
    queryKey: ['firearmCalibers', firearmSpecificId],
    queryFn: async () => {
      const response = await getFirearmCalibreClient().searchFirearmCalibres({
        filters: { firearmSpecificId },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmCalibreSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmCalibreClient().searchFirearmCalibres(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmCalibreById = (id: string) =>
  useQuery({
    queryKey: ['firearmCalibre', id],
    queryFn: async () => {
      const response = await getFirearmCalibreClient().getFirearmCalibreById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmCalibre = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmCalibreDTO) => {
      await getFirearmCalibreClient().createFirearmCalibre(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmCalibers'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmCalibre = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmCalibreDTO }) => {
      await getFirearmCalibreClient().updateFirearmCalibre(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmCalibers'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmCalibre = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmCalibreClient().deleteFirearmCalibre(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmCalibers'] });
    },
  });
};